import { useFormik } from "formik";
import * as Yup from "yup";
import React from "react";
import { useDispatch } from "react-redux";
import { setWorkingHours } from "../../../store/business/businessReducer";

const WorkingHours = ({
    onNext,
    onPrevious,
}: {
    onNext: () => void;
    onPrevious: () => void;
}) => {
    const dispatch = useDispatch();

    const daysOfWeek = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
    ];

    const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, "0"));
    const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, "0"));

    const validationSchema = Yup.object().shape(
        daysOfWeek.reduce((schema, day) => {
            schema[day.toLowerCase()] = Yup.array().of(
                Yup.object({
                    openHours: Yup.string().required(`${day} opening hour is required`),
                    openMinutes: Yup.string().required(`${day} opening minutes is required`),
                    closeHours: Yup.string().required(`${day} closing hour is required`),
                    closeMinutes: Yup.string().required(`${day} closing minutes is required`),
                })
            );
            return schema;
        }, {})
    );

    const formik = useFormik({
        initialValues: daysOfWeek.reduce((initial, day) => {
            initial[day.toLowerCase()] = [
                {
                    openHours: "",
                    openMinutes: "",
                    closeHours: "",
                    closeMinutes: "",
                },
            ];
            return initial;
        }, {}),
        validationSchema,
        onSubmit: (values) => {
            dispatch(setWorkingHours(values)); // Save to Redux state
            onNext(); // Proceed to the next step
        },
    });

    const addTimeSlot = (day: string) => {
        const newTimeSlot = {
            openHours: "",
            openMinutes: "",
            closeHours: "",
            closeMinutes: "",
        };
        formik.setFieldValue(day, [...formik.values[day], newTimeSlot]);
    };

    const removeTimeSlot = (day: string, index: number) => {
        const updatedSlots = formik.values[day].filter((_, i) => i !== index);
        formik.setFieldValue(day, updatedSlots);
    };

    return (
        <form onSubmit={formik.handleSubmit} className="space-y-6 px-4">
            <h2 className="text-lg font-bold mb-6 text-center">Working Hours with Breaks</h2>

            {daysOfWeek.map((day) => (
                <div key={day} className="space-y-4">
                    <h3 className="text-md font-semibold text-gray-700">{day}</h3>
                    {formik.values[day.toLowerCase()].map((timeSlot, index) => (
                        <div
                            key={index}
                            className="flex flex-wrap gap-4 items-center py-2 border-b last:border-none"
                        >
                            {/* Open Time */}
                            <div className="flex items-center gap-2 flex-1">
                                <select
                                    {...formik.getFieldProps(
                                        `${day.toLowerCase()}[${index}].openHours`
                                    )}
                                    className={`p-3 border rounded-md text-sm w-full ${formik.touched[day.toLowerCase()]?.[index]?.openHours &&
                                        formik.errors[day.toLowerCase()]?.[index]?.openHours
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } focus:ring-green-500 focus:border-green-500`}
                                >
                                    <option value="">HH</option>
                                    {hours.map((hour) => (
                                        <option key={hour} value={hour}>
                                            {hour}
                                        </option>
                                    ))}
                                </select>
                                :
                                <select
                                    {...formik.getFieldProps(
                                        `${day.toLowerCase()}[${index}].openMinutes`
                                    )}
                                    className={`p-3 border rounded-md text-sm w-full ${formik.touched[day.toLowerCase()]?.[index]?.openMinutes &&
                                        formik.errors[day.toLowerCase()]?.[index]?.openMinutes
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } focus:ring-green-500 focus:border-green-500`}
                                >
                                    <option value="">MM</option>
                                    {minutes.map((minute) => (
                                        <option key={minute} value={minute}>
                                            {minute}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <span className="text-gray-500 mx-2">to</span>

                            {/* Close Time */}
                            <div className="flex items-center gap-2 flex-1">
                                <select
                                    {...formik.getFieldProps(
                                        `${day.toLowerCase()}[${index}].closeHours`
                                    )}
                                    className={`p-3 border rounded-md text-sm w-full ${formik.touched[day.toLowerCase()]?.[index]?.closeHours &&
                                        formik.errors[day.toLowerCase()]?.[index]?.closeHours
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } focus:ring-green-500 focus:border-green-500`}
                                >
                                    <option value="">HH</option>
                                    {hours.map((hour) => (
                                        <option key={hour} value={hour}>
                                            {hour}
                                        </option>
                                    ))}
                                </select>
                                :
                                <select
                                    {...formik.getFieldProps(
                                        `${day.toLowerCase()}[${index}].closeMinutes`
                                    )}
                                    className={`p-3 border rounded-md text-sm w-full ${formik.touched[day.toLowerCase()]?.[index]?.closeMinutes &&
                                        formik.errors[day.toLowerCase()]?.[index]?.closeMinutes
                                        ? "border-red-500"
                                        : "border-gray-300"
                                        } focus:ring-green-500 focus:border-green-500`}
                                >
                                    <option value="">MM</option>
                                    {minutes.map((minute) => (
                                        <option key={minute} value={minute}>
                                            {minute}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Remove Button */}
                            {index > 0 && (
                                <button
                                    type="button"
                                    onClick={() => removeTimeSlot(day.toLowerCase(), index)}
                                    className="text-red-500 hover:text-red-700 sm:self-auto self-start"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}

                    {/* Add Additional Hours */}
                    <button
                        type="button"
                        onClick={() => addTimeSlot(day.toLowerCase())}
                        className="w-full p-2 border border-gray-300 rounded-md text-green-500 hover:text-green-700 hover:border-green-500"
                    >
                        + Add Additional Hours
                    </button>
                </div>
            ))}

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between">
                <button
                    type="button"
                    onClick={onPrevious}
                    className="px-4 py-2 bg-gray-300 text-gray-600 rounded hover:bg-gray-400"
                >
                    Previous
                </button>
                <button
                    type="submit"
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                    Next
                </button>
            </div>
        </form>
    );
};

export default WorkingHours;
